import type { QuizQuestion } from '@/types/domain';

/**
 * Per-learner test shuffling.
 *
 * Question order and answer-choice order are reordered for display, but the
 * server still grades against the ORIGINAL option order. So every shuffled
 * question carries an `optionMap` (display index → original index); a learner's
 * pick is mapped back with `toOriginalAnswer` before it is submitted. This keeps
 * grading correct with zero backend change.
 *
 * Shuffles are derived from a per-attempt `seed`, so they are stable across
 * re-renders within one sitting, vary per learner (each browser makes its own
 * seed), and re-roll on a retake.
 */

export interface ShuffledQuestion extends QuizQuestion {
  /** display index i shows the original option `optionMap[i]` */
  optionMap: number[];
}

/** A fresh seed for one attempt (per learner, per attempt). */
export function makeSeed(): number {
  return Math.floor(Math.random() * 0xffffffff);
}

/** mulberry32 — a tiny deterministic PRNG so a fixed seed gives a stable shuffle. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fisher–Yates with the given RNG. Returns a new array; never mutates input. */
export function shuffled<T>(arr: readonly T[], rng: () => number): T[] {
  const a = arr.slice();
  for (let k = a.length - 1; k > 0; k--) {
    const j = Math.floor(rng() * (k + 1));
    [a[k], a[j]] = [a[j], a[k]];
  }
  return a;
}

/** Reorder one question's options (never for True/False) and record the map
 *  back to the original indices. */
export function shuffleChoices(q: QuizQuestion, rng: () => number): ShuffledQuestion {
  const identity = q.options.map((_, idx) => idx);
  if (q.type === 'truefalse') return { ...q, optionMap: identity };
  const order = shuffled(identity, rng);
  return { ...q, options: order.map((orig) => q.options[orig]), optionMap: order };
}

/** Build the per-attempt view: optional question-order shuffle + optional
 *  per-question choice shuffle, all deterministic from `seed`. */
export function buildShuffledView(
  questions: QuizQuestion[],
  opts: { shuffleQuestions?: boolean; shuffleChoices?: boolean },
  seed: number,
): ShuffledQuestion[] {
  const rng = mulberry32(seed);
  const ordered = opts.shuffleQuestions ? shuffled(questions, rng) : questions.slice();
  return ordered.map((q) =>
    opts.shuffleChoices ? shuffleChoices(q, rng) : { ...q, optionMap: q.options.map((_, idx) => idx) },
  );
}

/** Convert a learner's picked DISPLAY index/indices back to ORIGINAL indices, so
 *  server grading (original option order) stays correct after a choice shuffle. */
export function toOriginalAnswer(map: number[], pick: number | number[]): number | number[] {
  return Array.isArray(pick) ? pick.map((d) => map[d]) : map[pick];
}
